import React, { useEffect, useRef, useState } from 'react';
import CloseIcon from 'assets/images/icons/close-40x40.svg';
import ScrollHintIcon from 'assets/images/icons/scroll-hint.svg';
import clsx from 'clsx';
import useStore from 'state/state';
import { arrayOf, object } from 'prop-types';

import useIsClickOutside from 'hooks/useIsClickOutside';
import GalleryItem from './GalleryItem/GalleryItem';
import styles from './Gallery.module.scss';

const Gallery = ({ data }) => {
  const [parsedData, setParsedData] = useState(null);
  const onClickOutside = useIsClickOutside();
  const [animateOut, setAnimateOut] = useState(false);
  const setIsGalleryOpen = useStore((state) => state.setIsGalleryOpen);
  const clickedGalleryImage = useStore((state) => state.clickedGalleryImage);
  const setClickedGalleryImage = useStore(
    (state) => state.setClickedGalleryImage
  );
  const [isScrolled, setIsScrolled] = useState(false);
  const [hideArrow, setHideArrow] = useState(false);
  const appData = useStore((state) => state.appData);

  const listRef = useRef(null);
  const selectedImageRef = useRef(null);
  const wrapperRef = useRef(null);

  const onScroll = () => {
    if (hideArrow) return;
    setTimeout(() => {
      setHideArrow(true);
    }, 500);
  };

  const onCloseGallery = () => {
    wrapperRef.current.removeEventListener('scroll', onScroll);
    setAnimateOut(true);
    setTimeout(() => {
      setClickedGalleryImage(null);
      setIsGalleryOpen(false);
    }, 500);
  };

  useEffect(() => {
    if (!selectedImageRef.current) return;
    if (!listRef.current) return;
    if (isScrolled) return;
    setTimeout(() => {
      setIsScrolled(true);
      wrapperRef.current.scrollTo(0, selectedImageRef.current.offsetTop - 150);
      setTimeout(() => {
        wrapperRef.current.addEventListener('scroll', onScroll);
      }, 500);
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listRef.current, selectedImageRef.current, clickedGalleryImage]);

  useEffect(() => {
    // console.log('gallery:', data);
    const parsedDataArr = [];
    data.forEach((entry, key) => {
      const obj = {
        index: key,
        key: `${key}-thumb`,
        imageUrl: '',
        type: '',
        text: '',
        videoCoverImage: '',
        youtubeVideoId: '',
      };

      obj.type = entry.type;
      obj.text = entry.text;

      if (entry.type === 'video') {
        if (entry.videoCoverImageUrl) {
          obj.videoCoverImage = entry.videoCoverImageUrl;
        }
        obj.youtubeVideoId = entry.youtubeVideoId;
      } else {
        obj.imageUrl = entry.imageUrl;
      }

      parsedDataArr.push(obj);
    });

    setParsedData(parsedDataArr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    onClickOutside.setRef(listRef);
    onClickOutside.setCallbackFunc(() => {
      onCloseGallery();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClickOutside]);

  useEffect(() => {
    if (clickedGalleryImage === data.length - 1) {
      setHideArrow(true);
    }
  }, [clickedGalleryImage, data, data.length]);

  return (
    <div
      ref={wrapperRef}
      className={clsx(styles.wrapper, { [styles.fadeOut]: animateOut })}
    >
      <div className={styles.tint} />
      <button
        type="button"
        label="close"
        onClick={onCloseGallery}
        className={styles.closeIcon}
      >
        <CloseIcon />
      </button>
      <div
        className={clsx(styles.labelPlacement, { [styles.fadeOut]: hideArrow })}
      >
        <div className={styles.label}>
          <span className={styles.text}>
            {appData.mainData.overallApp.scrollDownText}
          </span>
          <div className={styles.scrollDownIcon}>
            <ScrollHintIcon />
          </div>
        </div>
      </div>
      <div
        className={clsx(styles.list, { [styles.fadeOut]: animateOut })}
        ref={listRef}
      >
        {parsedData && (
          <>
            {parsedData.map(
              ({
                index,
                key,
                type,
                imageUrl,
                videoCoverImage,
                youtubeVideoId,
                text,
              }) => (
                <div
                  ref={clickedGalleryImage === index ? selectedImageRef : null}
                  key={key}
                  className={clsx(styles.galleryItem, {
                    [styles.above]: index < clickedGalleryImage,
                    [styles.selected]: clickedGalleryImage === index,
                    [styles.below]: index > clickedGalleryImage,
                  })}
                >
                  <GalleryItem
                    key={key}
                    type={type}
                    imageUrl={imageUrl}
                    videoCoverImage={videoCoverImage}
                    youtubeVideoId={youtubeVideoId}
                    text={text}
                  />
                </div>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
};

Gallery.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: arrayOf(object).isRequired,
};

export default Gallery;
